<script setup lang="ts">
import { computed, inject } from "vue";
import { ThemeCol, ThemeContainer, ThemeRow } from "@/ui-components";
import { type Breakpoints, useBreakpoints } from "@vueuse/core";
import { useCmsFooterConfig } from "@/composables/useCmsFooterConfig";
import TipTapRichText from "@/components/TipTapRichText.vue";
import ExternalLink from "@/components/MediaDetails/Modals/ExternalLink.vue";
import { isExternalUrl } from "@/utilities/isExternalUrl";

const breakpoints = useBreakpoints(inject("breakpoints") as Breakpoints<string>);
const smAndUp = computed(() => breakpoints.greaterOrEqual("sm").value);
const mdAndUp = computed(() => breakpoints.greaterOrEqual("md").value);
const lgAndUp = computed(() => breakpoints.greaterOrEqual("lg").value);

const { mainItems, supplementalItems, description, companions, logoUrl } = useCmsFooterConfig();
</script>

<template>
  <footer class="footer" :class="{ mdAndUp: mdAndUp }">
    <ThemeContainer>
      <ThemeRow>
        <ThemeCol md="6">
          <img class="logo" :src="logoUrl" style="width: 145px" alt="Logo" />
          <nav class="nav">
            <div v-for="(item, index) of mainItems" :key="index" class="nav-item">
              <ExternalLink v-if="item.isExternal" :url="item.url">
                <template #buttonOrLinkComponent>
                  <a class="nav-link" href="#">{{ item.label }}</a>
                </template>
              </ExternalLink>
              <router-link v-else class="nav-link" :to="item.url">{{ item.label }}</router-link>
            </div>
          </nav>
        </ThemeCol>
        <ThemeCol md="6">
          <div class="description" :class="{ smAndUp: smAndUp }">
            <TipTapRichText :content="description" />
          </div>
          <div class="companions" :class="{ smAndUp: smAndUp }">
            <template v-for="(companion, index) of companions" :key="index">
              <ExternalLink
                v-if="companion.url && isExternalUrl(companion.url)"
                :url="companion.url"
              >
                <template #buttonOrLinkComponent>
                  <a class="d-inline-block" href="#">
                    <img
                      class="companion"
                      :class="{ smAndUp: smAndUp, mdAndUp: mdAndUp, lgAndUp: lgAndUp }"
                      :src="companion.image.url"
                      :alt="companion.image.alternativeText || ''"
                    />
                  </a>
                </template>
              </ExternalLink>
              <component
                :is="companion.url ? 'a' : 'div'"
                v-else
                class="d-inline-block"
                :href="companion.url || null"
              >
                <img
                  class="companion"
                  :class="{ smAndUp: smAndUp, mdAndUp: mdAndUp, lgAndUp: lgAndUp }"
                  :src="companion.image.url"
                  :alt="companion.image.alternativeText || ''"
                />
              </component>
            </template>
          </div>
        </ThemeCol>
      </ThemeRow>
      <ThemeRow class="bottom">
        <ThemeCol>
          <nav class="sub-nav">
            <template v-for="(item, index) of supplementalItems" :key="index">
              <ExternalLink v-if="item.isExternal" :url="item.url">
                <template #buttonOrLinkComponent>
                  <a class="sub-nav-item" href="#">{{ item.label }}</a>
                </template>
              </ExternalLink>
              <router-link v-else class="sub-nav-item" :to="item.url">
                {{ item.label }}
              </router-link>
            </template>
          </nav>
        </ThemeCol>
      </ThemeRow>
    </ThemeContainer>
  </footer>
</template>

<style scoped lang="scss">
.footer {
  padding: 70px 0 50px;
  &.mdAndUp {
    padding: 144px 0 44px;
  }
}
.logo {
  margin-bottom: 24px;
  width: 140px;
}
.nav {
  display: flex;
  flex-direction: column;
  margin-bottom: 48px;
  margin-left: 37px;
  &-item:not(:last-child) {
    margin-bottom: 8px;
  }
  &-link {
    display: inline;
    padding: 0;
    font-family: D-DIN, serif;
    font-style: normal;
    font-weight: normal;
    font-size: 17px;
    line-height: 24px;
    color: var(--black);
    text-decoration: none;
  }
}
.description {
  margin-bottom: 32px;
  font-family: D-DIN, serif;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 24px;
  letter-spacing: 0.05em;
  color: var(--rolling-stone-approx);
  &.smAndUp {
    margin-bottom: 48px;
  }
}
.companions {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  row-gap: 30px;
  align-items: flex-start;
  column-gap: 30px;
  margin-bottom: 32px;
  &.smAndUp {
    margin-bottom: 48px;
  }
  .companion {
    width: auto;
    height: 75px;
    &.smAndUp {
      height: 75px;
    }
    &.mdAndUp {
      height: 45px;
    }
    &.lgAndUp {
      height: 75px;
    }
  }
}
.bottom {
  margin-top: 48px;
}
.sub-nav-item {
  font-family: D-DIN, serif;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 24px;
  letter-spacing: 0.05em;
  color: var(--rolling-stone-approx);
  text-decoration: none;
  &:not(:last-child) {
    margin-right: 16px;
  }
}
</style>
